import { EMPLOYEE_STATUS_LABELS } from '@company/shared'
import { Link } from '@tanstack/react-router'
import { Building2, Calendar, Clock, Mail, Phone, User, Users, Briefcase, FileText, ExternalLink } from 'lucide-react'

import { useEmployeeProfile , useEmployeeAvatar } from '@/hooks'
import { cn } from '@/lib/utils'

interface EmployeeProfileProps {
  employeeId: string
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  on_leave: 'bg-yellow-100 text-yellow-800',
  resigned: 'bg-gray-100 text-gray-800',
}

const ROLE_LABELS: Record<string, string> = {
  director: '主管',
  finance: '财务',
  hr: '人事',
  admin: '行政',
  pm: '项目经理',
  developer: '开发人员',
}

export function EmployeeProfile({ employeeId }: EmployeeProfileProps) {
  const { data: profile, isLoading, error } = useEmployeeProfile(employeeId)
  const { data: avatarData } = useEmployeeAvatar(employeeId)

  if (isLoading) {
    return <div className="flex items-center justify-center p-8">加载中...</div>
  }

  if (error || !profile) {
    return <div className="p-8 text-center text-destructive">加载失败</div>
  }

  const statusColor = STATUS_COLORS[profile.basic.status] ?? STATUS_COLORS.active
  const statusLabel = EMPLOYEE_STATUS_LABELS[profile.basic.status] ?? profile.basic.status

  return (
    <div className="space-y-6">
      {/* Basic Info Card */}
      <div className="rounded-lg border bg-card p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-2xl font-semibold text-primary">
            {avatarData?.url ? (
              <img src={avatarData.url} alt={profile.basic.name} className="h-full w-full object-cover" />
            ) : (
              profile.basic.name.charAt(0)
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">{profile.basic.name}</h2>
              <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', statusColor)}>
                {statusLabel}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">{profile.basic.employeeNo}</p>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><Mail className="h-4 w-4" />{profile.basic.email}</span>
              {profile.basic.phone && <span className="flex items-center gap-1"><Phone className="h-4 w-4" />{profile.basic.phone}</span>}
              <span className="flex items-center gap-1"><Calendar className="h-4 w-4" />入职: {profile.basic.hireDate}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Organization Info */}
      <div className="rounded-lg border bg-card p-6">
        <h3 className="mb-4 flex items-center gap-2 font-semibold"><Building2 className="h-5 w-5" />组织信息</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><span className="text-sm text-muted-foreground">角色</span><p className="font-medium">{ROLE_LABELS[profile.organization.role] ?? profile.organization.role}</p></div>
          <div><span className="text-sm text-muted-foreground">部门</span><p className="font-medium">{profile.organization.department?.name ?? '-'}</p></div>
          <div><span className="text-sm text-muted-foreground">职位</span><p className="font-medium">{profile.organization.position?.name ?? '-'}</p></div>
          <div><span className="text-sm text-muted-foreground">直属上级</span><p className="font-medium">{profile.organization.manager?.name ?? '-'}</p></div>
        </div>
      </div>

      {/* Teams */}
      <div className="rounded-lg border bg-card p-6">
        <h3 className="mb-4 flex items-center gap-2 font-semibold"><Users className="h-5 w-5" />团队 ({profile.teams.length})</h3>
        {profile.teams.length === 0 ? (
          <p className="text-sm text-muted-foreground">暂无团队</p>
        ) : (
          <div className="space-y-2">
            {profile.teams.map((team) => (
              <div key={team.id} className={cn('flex items-center justify-between rounded-md border p-3', !team.isActive && 'opacity-50')}>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{team.name}</p>
                    {team.isActive && (
                      <Link
                        to="/pm/teams/$teamId"
                        params={{ teamId: team.id }}
                        className="text-primary hover:text-primary/80"
                        title="查看团队"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Link>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {team.memberType === 'developer' ? team.developerGroup : team.functionRole}
                    {team.isLeader && ' · 负责人'}
                  </p>
                </div>
                <div className="text-right text-sm text-muted-foreground">
                  <p>加入: {team.joinDate}</p>
                  {team.endDate && <p>离开: {team.endDate}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>


      {/* Projects */}
      <div className="rounded-lg border bg-card p-6">
        <h3 className="mb-4 flex items-center gap-2 font-semibold"><Briefcase className="h-5 w-5" />项目 ({profile.projects.length})</h3>
        {profile.projects.length === 0 ? (
          <p className="text-sm text-muted-foreground">暂无项目</p>
        ) : (
          <div className="space-y-2">
            {profile.projects.map((project) => (
              <div key={project.id} className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <p className="font-medium">{project.name}</p>
                  <p className="text-sm text-muted-foreground">{project.projectNo}</p>
                </div>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{project.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Task Stats */}
      <div className="rounded-lg border bg-card p-6">
        <h3 className="mb-4 flex items-center gap-2 font-semibold"><FileText className="h-5 w-5" />任务统计</h3>
        <div className="grid grid-cols-4 gap-4 text-center">
          <div className="rounded-md bg-muted p-3">
            <p className="text-2xl font-semibold">{profile.taskStats.total}</p>
            <p className="text-sm text-muted-foreground">总计</p>
          </div>
          <div className="rounded-md bg-yellow-50 p-3">
            <p className="text-2xl font-semibold text-yellow-600">{profile.taskStats.pending}</p>
            <p className="text-sm text-muted-foreground">待处理</p>
          </div>
          <div className="rounded-md bg-blue-50 p-3">
            <p className="text-2xl font-semibold text-blue-600">{profile.taskStats.inProgress}</p>
            <p className="text-sm text-muted-foreground">进行中</p>
          </div>
          <div className="rounded-md bg-green-50 p-3">
            <p className="text-2xl font-semibold text-green-600">{profile.taskStats.completed}</p>
            <p className="text-sm text-muted-foreground">已完成</p>
          </div>
        </div>
      </div>

      {/* Leave Info */}
      <div className="rounded-lg border bg-card p-6">
        <h3 className="mb-4 flex items-center gap-2 font-semibold"><Clock className="h-5 w-5" />假期信息</h3>
        {profile.leave.currentlyOnLeave && (
          <div className="mb-4 rounded-md bg-yellow-50 p-3 text-sm text-yellow-800">当前正在休假中</div>
        )}
        <div className="grid gap-4 sm:grid-cols-3">
          {profile.leave.balances.map((balance) => (
            <div key={balance.id} className="rounded-md border p-3 text-center">
              <p className="text-sm text-muted-foreground">{balance.type}</p>
              <p className="text-lg font-semibold">{balance.total - balance.used} / {balance.total}</p>
              <p className="text-xs text-muted-foreground">剩余 / 总计</p>
            </div>
          ))}
        </div>
      </div>

      {/* Salary (if available) */}
      {profile.salary && (
        <div className="rounded-lg border bg-card p-6">
          <h3 className="mb-4 flex items-center gap-2 font-semibold"><User className="h-5 w-5" />薪资信息</h3>
          {profile.salary.current ? (
            <div className="grid gap-4 sm:grid-cols-3">
              <div><span className="text-sm text-muted-foreground">基本工资</span><p className="text-lg font-semibold">¥{(profile.salary.current.baseSalary / 100).toFixed(2)}</p></div>
              <div><span className="text-sm text-muted-foreground">津贴</span><p className="text-lg font-semibold">¥{(profile.salary.current.allowance / 100).toFixed(2)}</p></div>
              <div><span className="text-sm text-muted-foreground">生效日期</span><p className="text-lg font-semibold">{profile.salary.current.effectiveDate}</p></div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">暂无薪资记录</p>
          )}
        </div>
      )}

      {/* Timeline */}
      <div className="rounded-lg border bg-card p-6">
        <h3 className="mb-4 font-semibold">事件时间线</h3>
        {profile.timeline.length === 0 ? (
          <p className="text-sm text-muted-foreground">暂无事件</p>
        ) : (
          <div className="space-y-3">
            {profile.timeline.map((event) => (
              <div key={event.id} className="flex gap-3 border-l-2 border-muted pl-4">
                <div className="flex-1">
                  <p className="text-sm">{event.description}</p>
                  <p className="text-xs text-muted-foreground">{new Date(event.createdAt).toLocaleString('zh-CN')}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
